import { useMemo } from 'react';

import { useGetHotelEntities } from 'app/hooks/data/core/admin/hotels/useGetHotelEntities';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { toDayRoomAccessFormValue } from 'app/utils/dayRoomAccess';

import { sortPackagesByStatus } from '../_components/_utils/packageUtils';
import type { HotelActivity, HotelPackage, HotelRoom } from '../_types';

export const useHotelEntitiesData = (hotelId: number) => {
  const { data: hotel, isFetching: isFetchHotelLoading } = useGetHotel(hotelId);

  const {
    data: entitiesData,
    refetch: refetchEntities,
    isFetching: isFetchEntitiesLoading,
  } = useGetHotelEntities(hotelId);

  const packages = useMemo(
    () =>
      sortPackagesByStatus((entitiesData?.packages ?? []) as HotelPackage[]),
    [entitiesData]
  );

  const rooms = useMemo(
    () =>
      (entitiesData?.rooms.sort(
        (a, b) => (a.hotelPriority ?? 0) - (b.hotelPriority ?? 0)
      ) ?? []) as HotelRoom[],
    [entitiesData]
  );

  const activities = useMemo(
    () =>
      (entitiesData?.experiences.sort(
        (a, b) => (a.hotelPriority ?? 0) - (b.hotelPriority ?? 0)
      ) ?? []) as HotelActivity[],
    [entitiesData]
  );

  const dayRoomAccess = useMemo(
    () => toDayRoomAccessFormValue(entitiesData?.dayRoomAccess),
    [entitiesData]
  );

  return {
    hotel,
    isFetchHotelLoading,
    packages,
    rooms,
    activities,
    dayRoomAccess,
    isFetchEntitiesLoading,
    refetchPackages: refetchEntities,
    refetchRooms: refetchEntities,
    refetchActivities: refetchEntities,
  };
};
